import React from 'react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { Breakdown, Metrics } from './types';

const BreakdownChips: React.FC<{ title: string; items: Breakdown[]; upcase?: boolean }> = ({ title, items, upcase }) => (
  <Card variant="outlined" sx={{ flex: '1 1 220px', minWidth: 220 }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
        {items.length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
        {items.map((b) => (
          <Chip
            key={b.key}
            size="small"
            label={`${upcase ? b.key.toUpperCase() : b.key}: ${b.billable}${b.reruns ? ` (+${b.reruns} rerun${b.reruns === 1 ? '' : 's'})` : ''}`}
          />
        ))}
      </Box>
    </CardContent>
  </Card>
);

export const StatCards: React.FC<{ metrics: Metrics }> = ({ metrics }) => {
  const { totals } = metrics;
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Card sx={{ flex: '0 0 auto', minWidth: 180, bgcolor: 'success.light' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" sx={{ color: 'success.contrastText' }}>
            Billable versions
          </Typography>
          <Typography variant="h4" sx={{ color: 'success.contrastText', fontWeight: 700 }}>
            {totals.billable}
          </Typography>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ flex: '0 0 auto', minWidth: 160 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Reruns (not billed)
          </Typography>
          <Typography variant="h4" color="text.secondary" sx={{ fontWeight: 700 }}>
            {totals.reruns}
          </Typography>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ flex: '0 0 auto', minWidth: 160 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Total renders
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {totals.all}
          </Typography>
          {totals.undated > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totals.undated} without a timestamp
            </Typography>
          )}
        </CardContent>
      </Card>
      <BreakdownChips title="Billable by chart" items={metrics.byChart} />
      <BreakdownChips title="By orientation" items={metrics.byOrientation} />
      <BreakdownChips title="By language" items={metrics.byLanguage} upcase />
    </Box>
  );
};
